/**
 * Etiquetas en castellano de los ENUMS REALES de `dominio/conductoresApi.ts`.
 *
 * 🔴 **Deliberadamente NO reusan `componentes/Etiqueta.tsx`.** Sus diccionarios están indexados
 * por los tipos MOCK de `dominio/contratos.ts`, y para conductores TRES de los CUATRO no coinciden
 * con lo que el backend produce (medido contra `apps/api/prisma/schema.prisma` — ver la tabla en
 * la cabecera de `dominio/conductoresApi.ts`):
 *
 * ```
 *   real DADA_DE_BAJA   → el mock tiene DE_BAJA        ⇒ se pintaría «DADA_DE_BAJA» crudo
 *   real DESCONECTADO   → el mock tiene FUERA_DE_LINEA ⇒ se pintaría «DESCONECTADO» crudo
 *   real PENDIENTE y SUSPENDIDO (verificación)         ⇒ no existen en el mock, crudos los dos
 * ```
 *
 * Y el modo de falla de `Etiqueta.tsx` ante un valor que no conoce es `[valor, 'neutro']` — o sea
 * **no rompe, no avisa y pinta el identificador técnico en gris**, que es exactamente el fallo
 * silencioso: en pantalla se ve una etiqueta más, no un error. Mismo criterio que ya adoptaron
 * `Verificacion.tsx` e `Incidentes.tsx` para su propio hueco de tipos.
 *
 * 🔑 **La cuarta (`EstadoSuscripcion`) SÍ coincide valor por valor con el mock**, y aun así se
 * declara acá: tenerla al lado de las otras tres deja las cuatro con la misma fuente (los enums
 * reales) en vez de dos pantallas leyendo dos diccionarios distintos según el campo. Si el mock
 * se corrigiera o se rompiera, estas etiquetas no se enteran, que es lo que se busca.
 *
 * Mismas clases CSS (`etiqueta etiqueta--<tono>`) que el resto de la consola: se ve igual.
 *
 * ⚠️ **La lectura es por búsqueda con fallback, no por índice directo.** Los DTO del backend
 * declaran varios de estos campos como `string` ancho (`suscripcion: { estado: string }`,
 * `vehiculo: { tipo: string }`) aunque hoy sólo puedan producir valores del enum: si algún día
 * aparece uno nuevo, esto lo muestra crudo en vez de romper la fila entera.
 */
import type {
  EstadoCuentaReal,
  EstadoDisponibilidadReal,
  EstadoSuscripcionReal,
  EstadoVerificacionReal,
  TipoVehiculoReal,
} from '../dominio/conductoresApi';
import type { Tono } from './Etiqueta';

const VERIFICACION: Record<EstadoVerificacionReal, [string, Tono]> = {
  PENDIENTE: ['Pendiente', 'neutro'],
  EN_REVISION: ['En revisión', 'atencion'],
  APROBADO: ['Aprobado', 'bueno'],
  RECHAZADO: ['Rechazado', 'malo'],
  SUSPENDIDO: ['Suspendido', 'malo'],
};

const DISPONIBILIDAD: Record<EstadoDisponibilidadReal, [string, Tono]> = {
  DESCONECTADO: ['Desconectado', 'neutro'],
  DISPONIBLE: ['Disponible', 'bueno'],
  OCUPADO: ['En viaje', 'curso'],
};

const CUENTA: Record<EstadoCuentaReal, [string, Tono]> = {
  ACTIVA: ['Activa', 'bueno'],
  SUSPENDIDA: ['Suspendida', 'malo'],
  DADA_DE_BAJA: ['Dada de baja', 'neutro'],
};

/**
 * ⚠️ `PENDIENTE` es «esperando el cobro», no «pendiente de revisión»: es el mismo identificador
 * que en `VERIFICACION` con otro significado, y por eso el texto que ve el operador es distinto.
 * `ACTIVA` es lo único que habilita entrar al dispatch (R-01).
 */
const SUSCRIPCION: Record<EstadoSuscripcionReal, [string, Tono]> = {
  SIN_INICIAR: ['Sin iniciar', 'neutro'],
  PENDIENTE: ['Esperando el cobro', 'atencion'],
  ACTIVA: ['Activa', 'bueno'],
  FALLIDA: ['Cobro rechazado', 'malo'],
  VENCIDA: ['Vencida', 'malo'],
  CANCELADA: ['Cancelada', 'neutro'],
};

/** 📌 `MOTOTAXI` retirado el 2026-09-01 con la clase (ver `dominio/conductoresApi.ts`). */
export const TIPO_VEHICULO_REAL: Record<TipoVehiculoReal, string> = {
  AUTO: 'Auto',
};

const DICCIONARIOS = {
  verificacion: VERIFICACION,
  disponibilidad: DISPONIBILIDAD,
  cuenta: CUENTA,
  suscripcion: SUSCRIPCION,
} as const;

type Diccionario = keyof typeof DICCIONARIOS;

function leer(tipo: Diccionario, valor: string): [string, Tono] {
  const tabla = DICCIONARIOS[tipo] as Record<string, [string, Tono] | undefined>;
  return tabla[valor] ?? [valor, 'neutro'];
}

/** El tono de un estado real, para pintar la cinta del canto de la fila. */
export const tonoRealDe = (tipo: Diccionario, valor: string): Tono => leer(tipo, valor)[1];

export function EtiquetaConductor({ tipo, valor }: { tipo: Diccionario; valor: string }) {
  const [texto, tono] = leer(tipo, valor);
  return <span className={`etiqueta etiqueta--${tono}`}>{texto}</span>;
}

/** El nombre en castellano de una clase de vehículo, tolerante a un valor que no conocemos. */
export const tipoVehiculoTexto = (valor: string): string =>
  (TIPO_VEHICULO_REAL as Record<string, string | undefined>)[valor] ?? valor;
